import React from 'react'
import { CBadge } from '@coreui/react-pro'
import { RecordSelect, matchRecord } from './playhqShared'

// What makes grades, teams and players different inside LinkImporter.

const gradeLabel = (g) => g.grade

export const gradeConfig = {
  noun: 'grade',
  nounPlural: 'grades',
  recordsKey: 'our_grades',
  loadingText: 'Loading grades from PlayHQ…',
  intro:
    'Already made your comps? Choose "Link to existing (keep my name)" and pick yours - nothing is renamed or created; your grade just learns which PlayHQ grade it is. "Link & rename" also renames yours to PlayHQ’s name. PlayHQ gives grades new ids each season, so relink at the start of each season.',
  getRecordName: (r) => r.grade,
  getRecordLabel: gradeLabel,
  itemName: (item) => item.name,
  itemPlayhqId: (item) => item.playhq_id,
  itemColumns: [{ header: 'Your club’s PlayHQ teams', cell: (row) => row.item.teams.join(', ') }],
  extraColumns: [],
  initialExtra: () => ({}),
  toPayload: (row) => ({
    playhq_id: row.item.playhq_id,
    name: row.item.name,
    action: row.action,
    grade_id: row.target_id || null,
  }),
}

const teamLabel = (t) => `${t.name}${t.grade ? ` (${t.grade})` : ''}`

export const teamConfig = {
  noun: 'team',
  nounPlural: 'teams',
  recordsKey: 'our_teams',
  loadingText: 'Loading teams from PlayHQ…',
  intro:
    'Already made your teams? Choose "Link to existing (keep my name)" and pick yours - nothing is renamed or created, and the Fixtures and Squads tabs then pick your team for that PlayHQ team. Link your grades first and each team is suggested from its grade. "Create new" adds a new team from PlayHQ.',
  getRecordName: (r) => r.name,
  getRecordLabel: teamLabel,
  itemName: (item) => item.name,
  itemPlayhqId: (item) => item.playhq_id,
  itemColumns: [{ header: 'PlayHQ grade', cell: (row) => row.item.grade_name }],
  // Ticked rows can be given a grade at once - used when they are created or renamed.
  bulkFields: [
    {
      key: 'grade_id',
      label: 'Grade',
      placeholder: 'Set grade…',
      recordsKey: 'our_grades',
      getLabel: gradeLabel,
      appliesTo: (row) => row.action === 'create' || row.action === 'update',
    },
  ],
  extraColumns: [
    {
      header: 'Grade',
      cell: (row, update, data) =>
        row.action === 'create' || row.action === 'update' ? (
          <RecordSelect
            value={row.grade_id}
            onChange={(grade_id) => update({ grade_id })}
            records={data.our_grades}
            getLabel={gradeLabel}
            placeholder={row.action === 'update' ? 'Keep current grade' : 'Choose grade…'}
            invalid={row.action === 'create' && !row.grade_id}
          />
        ) : (
          <span className="small text-medium-emphasis">Unchanged</span>
        ),
    },
  ],
  // The grade linked to this PlayHQ grade, else one with the same name.
  initialExtra: (item, data) => {
    const found = matchRecord(data.our_grades || [], item.grade_id, item.grade_name, (g) => g.grade)
    return { grade_id: found.record ? String(found.record.id) : '' }
  },
  // A PlayHQ team in one of your grades (linked, or the same name) is your only team in that
  // grade not already taken by another row: Deakin - 1st XI → your "Team1" in the same comp.
  suggest: (item, data, ours, claimed) => {
    const grade = matchRecord(
      data.our_grades || [],
      item.grade_id,
      item.grade_name,
      (g) => g.grade,
    ).record
    if (!grade) return null
    const inGrade = ours.filter(
      (t) => String(t.grade_id) === String(grade.id) && !claimed.has(String(t.id)),
    )
    return inGrade.length === 1 ? inGrade[0] : null
  },
  rowProblem: (row) =>
    row.action === 'create' && !row.grade_id ? 'Choose a grade for the new team.' : null,
  describeNew: (row, data) => {
    const grade = (data.our_grades || []).find((g) => String(g.id) === String(row.grade_id))
    return `${row.item.name}${grade ? ` (${grade.grade})` : ''}`
  },
  toPayload: (row) => ({
    playhq_id: row.item.playhq_id,
    name: row.item.name,
    action: row.action,
    team_id: row.target_id || null,
    grade_id: row.grade_id || null,
  }),
}

const playerLabel = (p) =>
  `${p.full_name}${p.team_name ? ` · ${p.team_name}` : ''}${
    Number(p.is_active) === 0 ? ' (inactive)' : ''
  }`

export const playerConfig = {
  noun: 'player',
  nounPlural: 'players',
  recordsKey: 'our_players',
  loadingText: 'Reading every match your teams played - this can take a few seconds…',
  intro:
    'Players are everyone PlayHQ lists for your club in these matches. Link the ones already in your game so future imports find them by PlayHQ id.',
  reviewNote:
    'New players are added as batters (wicket keepers as WK) at your lowest price. Set their positions and prices on the Players page afterwards.',
  // A club has plenty of players PlayHQ lists that are new to the game.
  createWhenUnmatched: true,
  getRecordName: (r) => r.full_name,
  getRecordLabel: playerLabel,
  itemName: (item) => item.name,
  itemPlayhqId: (item) => item.participant_id,
  itemColumns: [
    {
      header: 'Played for',
      cell: (row) => (
        <>
          <div className="small">
            {row.item.teams.map((t) => `${t.name} (${t.matches})`).join(', ')}
          </div>
          {row.item.is_keeper && (
            <CBadge color="dark" className="fw-normal">
              WK
            </CBadge>
          )}
        </>
      ),
    },
  ],
  bulkFields: [
    {
      key: 'team_id',
      label: 'Team',
      placeholder: 'Set team…',
      recordsKey: 'our_teams',
      getLabel: teamLabel,
      appliesTo: (row) => row.action === 'create' || row.action === 'update',
    },
  ],
  extraColumns: [
    {
      header: 'Team',
      cell: (row, update, data) =>
        row.action === 'create' || row.action === 'update' ? (
          <RecordSelect
            value={row.team_id}
            onChange={(team_id) => update({ team_id })}
            records={data.our_teams}
            getLabel={teamLabel}
            placeholder={row.action === 'update' ? 'Keep current team' : 'No team'}
          />
        ) : (
          <span className="small text-medium-emphasis">Unchanged</span>
        ),
    },
  ],
  initialExtra: (item) => ({
    team_id: item.suggested_team_id ? String(item.suggested_team_id) : '',
  }),
  describeNew: (row, data) => {
    const team = (data.our_teams || []).find((t) => String(t.id) === String(row.team_id))
    return `${row.item.name} · ${row.item.is_keeper ? 'WK' : 'BAT'}${team ? ` · ${team.name}` : ''}`
  },
  toPayload: (row) => ({
    participant_id: row.item.participant_id,
    first_name: row.item.first_name,
    last_name: row.item.last_name,
    is_keeper: row.item.is_keeper,
    action: row.action,
    player_id: row.target_id || null,
    team_id: row.team_id || null,
  }),
}
